/**
 * <s-ambassador-letter> — Velle®Collabs floating envelope with a blue wax seal.
 * Clicking the envelope breaks the seal, opens the flap and lifts the letter. The rising letter then morphs
 * into the full application letter (the contact form) with a View Transition named "velle-letter", while the
 * envelope fades away behind it. Browsers without View Transitions fade the envelope out, then fade the form in.
 *
 * States (data-state on the host): closed → opening → open. data-flap="back" is set mid-animation so the
 * letter can rise above the opened flap; data-fade="out" fades the envelope in the fallback path.
 * Reduced motion jumps straight to open.
 * Required DOM: [data-envelope-trigger] (button), [data-letter-peek] (small letter inside the envelope),
 * [data-letter] (form card, hidden until open). The host renders data-state="open" after a submit.
 */
class AmbassadorLetter extends HTMLElement {
  #ac;
  #timers = [];

  connectedCallback() {
    this.#ac = new AbortController();
    const trigger = this.querySelector('[data-envelope-trigger]');
    trigger?.addEventListener('click', () => this.#open(), { signal: this.#ac.signal });
    if (this.dataset.state === 'open') this.#show(false);
  }

  disconnectedCallback() {
    this.#ac?.abort();
    this.#timers.forEach(clearTimeout);
  }

  #open() {
    if (this.dataset.state !== 'closed') return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return this.#show(true);
    this.querySelector('[data-envelope-trigger]')?.setAttribute('aria-expanded', 'true');
    this.dataset.state = 'opening';
    this.#timers.push(setTimeout(() => { this.dataset.flap = 'back'; }, 560));

    if (typeof document.startViewTransition === 'function') {
      // Letter finishes rising at ~1.75s; morph it into the form right after.
      this.#timers.push(setTimeout(() => this.#morph(), 1800));
    } else {
      this.#timers.push(setTimeout(() => { this.dataset.fade = 'out'; }, 1900));
      this.#timers.push(setTimeout(() => this.#show(true), 2450));
    }
  }

  #morph() {
    const peek = this.querySelector('[data-letter-peek]');
    const letter = this.querySelector('[data-letter]');
    if (!peek || !letter) return this.#show(true);
    peek.style.viewTransitionName = 'velle-letter';
    letter.style.animation = 'none';
    const transition = document.startViewTransition(() => {
      peek.style.viewTransitionName = '';
      letter.style.viewTransitionName = 'velle-letter';
      this.#show(false);
    });
    transition.finished.finally(() => {
      letter.style.viewTransitionName = '';
      this.#focusFirst(letter);
    });
  }

  #show(focus) {
    this.dataset.state = 'open';
    this.querySelector('[data-envelope-trigger]')?.setAttribute('aria-expanded', 'true');
    const letter = this.querySelector('[data-letter]');
    if (!letter) return;
    letter.hidden = false;
    if (focus) this.#focusFirst(letter);
  }

  #focusFirst(letter) {
    letter.querySelector('input:not([type="hidden"]), textarea, select')?.focus({ preventScroll: true });
  }
}

customElements.define('s-ambassador-letter', AmbassadorLetter);
